import {
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  Divider,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import userService from "../../../services/userService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  tabLabel,
  controlLabel,
  HIDDEN_TABS,
} from "../../../utils/permissionLabels";

const AddUserModal = ({ open, handleClose, addUser, editingUser = null }) => {
  const defaultFormState = {
    name: "",
    email: "",
    active: true,
    rol: "operador",
    permissions: [],
  };

  const [user, setUser] = useState(defaultFormState);
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [Permissions, setPermissions] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    const fetchPermits = async () => {
      if (open) {
        try {
          const response = await userService.getPermits();
          setPermissions(response.data);
        } catch (error) {
          console.error("Error al obtener permisos:", error);
        }
      }
    };
    fetchPermits();
  }, [open]);

  useEffect(() => {
    if (editingUser) {
      setUser({
        ...editingUser,
        rol: editingUser.is_seller ? "vendedor" : "operador",
        permissions: editingUser.permissions || [],
      });
      setIsEditing(true);
    } else {
      setUser(defaultFormState);
      setIsEditing(false);
    }
  }, [editingUser, open]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!user.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    if (!user.email.trim()) {
      newErrors.email = "El email es obligatorio";
    } else if (!validateEmail(user.email)) {
      newErrors.email = "Email inválido";
    }

    // El colaborador define su propia contraseña desde el link del mail de
    // invitación ("Invite user"); el owner ya no la elige en el alta.

    if (user.permissions.length === 0) {
      newErrors.permissions = "Debe seleccionar al menos un permiso";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handlePermissionChange = (permissionId) => {
    const exists = user.permissions.some((perm) => perm.id === permissionId);

    const updatedPermissions = exists
      ? user.permissions.filter((perm) => perm.id !== permissionId)
      : [...user.permissions, { id: permissionId }];

    setUser({ ...user, permissions: updatedPermissions });

    if (updatedPermissions.length > 0 && errors.permissions) {
      setErrors({ ...errors, permissions: null });
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setIsLoading(true); // Activar el spinner

      const userToSubmit = {
        name: user.name,
        email: user.email,
        active: user.active,
        // Los colaboradores nunca son admins; el rol elige Operador vs Vendedor.
        is_owner: false,
        is_seller: user.rol === "vendedor",
        permissions: user.permissions,
      };
      // No se envía contraseña: el backend, al no recibirla, dispara el invite
      // de Supabase ("Invite user") y el colaborador la define desde el mail.

      try {
        await addUser(userToSubmit, isEditing);
        setSnackbarMessage(
          isEditing
            ? "Colaborador actualizado correctamente"
            : "Colaborador creado correctamente"
        );
        setSnackbarOpen(true);
        handleReset();
        setTimeout(() => {
          setIsLoading(false);
          handleClose();
        }, 1500);
      } catch (error) {
        setIsLoading(false);
        setSnackbarMessage("Error al procesar la solicitud");
        setSnackbarOpen(true);
      }
    }
  };

  const handleReset = () => {
    setUser(defaultFormState);
    setErrors({});
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const groupPermissionsByTab = (permissions) => {
    return permissions.reduce((acc, permission) => {
      // Ocultamos tabs sin funcionalidad/enforcement (roles) para no ofrecer
      // permisos sin efecto.
      if (HIDDEN_TABS.includes(permission.tab)) {
        return acc;
      }
      if (!acc[permission.tab]) {
        acc[permission.tab] = [];
      }
      acc[permission.tab].push(permission);
      return acc;
    }, {});
  };

  const groupedPermissions = groupPermissionsByTab(Permissions);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 2",
            paddingBottom: "15px",
          }}
        >
          <Typography variant="h6">
            {isEditing ? "Editar colaborador" : "Nuevo colaborador"}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          {/* Use form element with correct autocomplete attribute */}
          <form
            autoComplete="off"
            id="user-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <Grid container spacing={2} sx={{ marginTop: "10px" }}>
              {/* Nombre */}
              <Grid item xs={6}>
                <TextField
                  name="name"
                  label="Nombre completo"
                  variant="outlined"
                  fullWidth
                  value={user.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  InputProps={{
                    sx: { borderRadius: "8px" },
                    autoComplete: "new-password", // This forces browsers to not autofill
                  }}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  value={user.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    sx: { borderRadius: "8px" },
                    autoComplete: "new-password", // This forces browsers to not autofill
                  }}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="rol-label">Rol</InputLabel>
                  <Select
                    labelId="rol-label"
                    label="Rol"
                    name="rol"
                    value={user.rol}
                    onChange={handleChange}
                    sx={{ borderRadius: "8px" }}
                  >
                    <MenuItem value="operador">Operador</MenuItem>
                    <MenuItem value="vendedor">Vendedor</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!isEditing && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Alert severity="info">
                      Le enviaremos una invitación por email. Al aceptarla,
                      definirá su propia contraseña y podrá ingresar a Voras.
                    </Alert>
                  </Box>
                </Grid>
              )}
              {isEditing && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info">
                      Los campos de contraseña se omiten al editar un usuario
                      existente. Para cambiar la contraseña, utilice la opción
                      "Restablecer contraseña".
                    </Alert>
                  </Box>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControl
                fullWidth
                error={!!errors.permissions}
                component="fieldset"
                variant="outlined"
              >
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                  Permisos
                </Typography>

                {Object.entries(groupedPermissions).map(
                  ([tab, permissions]) => (
                    <Accordion
                      key={tab}
                      expanded={expandedAccordion === tab}
                      onChange={handleAccordionChange(tab)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{tabLabel(tab)}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {permissions.map((permission) => (
                            <Grid item xs={6} key={permission.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={user.permissions.some(
                                      (p) => p.id === permission.id
                                    )}
                                    onChange={() =>
                                      handlePermissionChange(permission.id)
                                    }
                                    name={permission.id}
                                    color="primary"
                                  />
                                }
                                label={controlLabel(permission.control, tab)}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
                {errors.permissions && (
                  <Typography color="error" variant="caption">
                    {errors.permissions}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={handleClose}
            color="inherit"
            sx={{ borderRadius: "8px" }}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "8px",
              position: "relative",
              minWidth: "100px",
            }}
            disabled={isLoading}
            type="submit"
            form="user-form"
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEditing ? (
              "Actualizar"
            ) : (
              "Guardar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
};

export default AddUserModal;
